import React from "react";
import { Quote, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { getIcon } from "./icons";
import { useSiteContent } from "../../context/SiteContent";

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} star rating`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={15} className={i < n ? "fill-helios-amber text-helios-amber" : "text-slate-300"} />
    ))}
  </div>
);

export const Reviews = () => {
  const { content } = useSiteContent();
  const reviews = (content?.testimonials || []).filter((t) => t.isPublished);
  const badges = (content?.certificationsAndBadges || []).filter((b) => b.isActive);

  return (
    <section id="reviews" className="py-24 md:py-32 bg-helios-mist">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal className="max-w-2xl mb-14">
          <p className="overline text-helios-cyan mb-3">Reviews &amp; Social Proof</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-helios-slate leading-[1.05]">
            Trusted by plant heads &amp; O&amp;M teams.
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((rv, i) => (
            <Reveal key={rv.id || i} delay={i * 0.08}>
              <div className="card-hover h-full flex flex-col rounded-2xl border border-helios-line bg-white p-7" data-testid={`review-${i}`}>
                <div className="flex items-center justify-between">
                  <Stars n={rv.rating || 5} />
                  <Quote size={22} className="text-helios-amber" />
                </div>
                <p className="mt-4 text-helios-slate leading-relaxed flex-1">"{rv.quote}"</p>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-helios-line">
                  {rv.img && <img src={rv.img} alt={rv.clientName} className="w-11 h-11 rounded-full object-cover" />}
                  <div>
                    <p className="font-display font-bold text-sm text-helios-slate">{rv.clientName}</p>
                    <p className="text-xs text-slate-500">{[rv.designation, rv.companyName].filter(Boolean).join(" · ")}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {badges.map((b, i) => {
              const Icon = getIcon(b.iconName);
              return (
                <div key={b.id || i} className="flex flex-col gap-3 rounded-2xl border border-helios-line bg-white p-5" data-testid={`trust-badge-${i}`}>
                  <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-helios-cyan/10 text-helios-cyan shrink-0"><Icon size={22} /></div>
                  <div>
                    <p className="font-display font-bold text-sm text-helios-slate leading-tight">{b.title}</p>
                    <p className="font-mono text-[10.5px] text-slate-500 mt-1">{b.subtitle}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
